/**
 * Utility functions and hooks for CopyableValue components
 */

import React, { useState, useEffect } from 'react';
import type { TooltipPosition, CopyableValueVariant } from './types';

/**
 * Hook for calculating tooltip position with viewport collision detection
 *
 * @param triggerRef - Reference to the trigger element
 * @param isVisible - Whether the tooltip should be visible
 * @param tooltipWidth - Width of the tooltip (default: 220px)
 * @param tooltipHeight - Height of the tooltip (default: 80px)
 * @returns Position object with top, left, and arrowLeft coordinates
 */
export function useTooltipPosition(
  triggerRef: React.RefObject<HTMLElement>,
  isVisible: boolean,
  tooltipWidth: number = 220,
  tooltipHeight: number = 80,
  gap: number = 10,
  gapBelow?: number
): TooltipPosition {
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    arrowLeft: 17,
    placement: 'above'
  });

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      // Use separate gaps for above and below
      const gapAbove = gap;
      const gapBelowValue = gapBelow ?? gap;

      // Calculate position above the trigger
      let top = rect.top - tooltipHeight - gapAbove;
      let arrowLeft = tooltipWidth / 2;
      let placement: 'above' | 'below' = 'above';

      // If tooltip would go above viewport, show below instead
      if (top < 10) {
        top = rect.bottom + gapBelowValue;
        placement = 'below';
      }

      // Calculate horizontal position centered on trigger
      const viewportWidth = window.innerWidth;
      const triggerCenter = rect.left + rect.width / 2;
      let left = triggerCenter - tooltipWidth / 2;

      // Adjust if it would go off the left edge
      if (left < 10) {
        arrowLeft = Math.max(17, triggerCenter - 10);
        left = 10;
      }

      // Adjust if it would go off the right edge
      if (left + tooltipWidth > viewportWidth - 10) {
        const rightEdge = viewportWidth - 10;
        arrowLeft = Math.min(tooltipWidth - 17, tooltipWidth - (rightEdge - triggerCenter));
        left = rightEdge - tooltipWidth;
      }

      setPosition({ top, left, arrowLeft, placement });
    }
  }, [isVisible, tooltipWidth, tooltipHeight, gap, gapBelow]);

  return position;
}

/**
 * Copies text to clipboard with fallback for older browsers
 *
 * @param text - Text to copy to clipboard
 * @param onSuccess - Callback fired after successful copy
 * @param onError - Callback fired if copy fails
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyToClipboard(
  text: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void
): Promise<boolean> {
  try {
    // Try modern clipboard API first
    await navigator.clipboard.writeText(text);
    onSuccess?.();
    return true;
  } catch (err) {
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        onSuccess?.();
        return true;
      } else {
        throw new Error('Fallback copy failed');
      }
    } catch (fallbackErr) {
      onError?.(fallbackErr as Error);
      return false;
    }
  }
}

/**
 * Returns the appropriate SVG icon for a given variant
 *
 * @param variant - The variant type
 * @returns React node containing the SVG icon
 */
export function getIconForVariant(variant: CopyableValueVariant): React.ReactNode {
  switch (variant) {
    case 'summary':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
      );

    case 'id':
    case 'generic':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M7 7h10M7 12h10M7 17h6" />
        </svg>
      );

    case 'member':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );

    case 'system':
      return (
        <svg className="main-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <line x1="9" y1="1" x2="9" y2="4" />
          <line x1="15" y1="1" x2="15" y2="4" />
          <line x1="9" y1="20" x2="9" y2="23" />
          <line x1="15" y1="20" x2="15" y2="23" />
          <line x1="20" y1="9" x2="23" y2="9" />
          <line x1="20" y1="15" x2="23" y2="15" />
          <line x1="1" y1="9" x2="4" y2="9" />
          <line x1="1" y1="15" x2="4" y2="15" />
        </svg>
      );

    default:
      return null;
  }
}

/**
 * Returns the copy indicator SVG icon
 */
export function getCopyIndicatorIcon(): React.ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/**
 * Returns the checkmark SVG icon used in success tooltips
 */
export function getCheckmarkIcon(): React.ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
